import { useEffect, useMemo, useRef, useState } from "react";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { authedFetch } from "../services/authedFetch";
import { PUBLIC_BASE_URL } from "../config/publicBaseUrl";
import { useNavigate } from "react-router-dom";
import { logoutAndClearSite } from "../services/logout";
import { db } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { getActiveSiteId, getActiveTenantId } from "../services/activeTenant";
import {
  getReferralPendingPath,
  getReferralRecipientsPath,
  getReferralRecipientMrnDocPath,
} from "../services/firestorePaths";
import { extractReferralScheduleReport } from "../services/referralScheduleService";
import { reviewReasons } from "../services/referralRowReview";
import {
  buildRescheduleFields,
  classifyImportedRow,
  collapseSamePatientInBatch,
  pickRowToKeep,
} from "../services/referralRescheduleMatch";
import { useTenant } from "../context/TenantContext";
import {
  Phone,
  Plus,
  Trash2,
  Send,
  Loader2,
  Library,
  LogOut,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  History,
  LayoutDashboard,
  Upload,
  FileText,
  Pencil,
  Check,
  X,
  MapPin,
  Calendar,
  Clock,
  CalendarClock,
  CalendarCheck,
  Hash,
  Users,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Filter,
} from "lucide-react";

// ---------------------------------------------------------------------------
// SWAP-TOMORROW LINK BASE
// ---------------------------------------------------------------------------
// Every recipient gets a unique token, and the texted link is
// `${REFERRAL_LINK_BASE}/${token}`. Today this points at the existing e-sign
// route as a placeholder. TOMORROW: once the questionnaire+esign flow exists,
// change ONLY this one string to its base path. The per-recipient token is
// already persisted on each referral_recipients doc, so the new flow can look
// up who each link belongs to with zero other changes here.
const REFERRAL_LINK_BASE = `${PUBLIC_BASE_URL}/referral-form`;

const SEND_MASS_REFERRAL_SMS_URL =
  `${FUNCTIONS_BASE}/sendMassReferralSms`;

// Mirror the reception consent flow's token style (UUID, hyphens stripped).
const makeToken = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID().replace(/-/g, "")
    : `${Math.random().toString(36).slice(2)}${Math.random()
        .toString(36)
        .slice(2)}`;

// Match ReceptionPage: prepend +1 when the user didn't type a country code.
const normalizePhone = (raw) => {
  const p = String(raw || "").trim();
  if (!p) return "";
  return p.startsWith("+") ? p : `+1${p}`;
};

// Strict US mobile normalizer used when editing an imported row: returns
// +1XXXXXXXXXX for a valid 10-digit (or 11-digit leading-1) number, else "".
// Mirrors the server's normalizeMobile so "sendable" means the same thing.
const toE164 = (raw) => {
  const d = String(raw || "").replace(/\D/g, "");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d[0] === "1") return `+${d}`;
  return "";
};

const millis = (ts) => (ts && ts.toMillis ? ts.toMillis() : 0);
const fmtTime = (ts) =>
  ts && ts.toDate ? ts.toDate().toLocaleString() : "—";

// Sortable "YYYY-MM-DD HH:mm" key from a row's appointment date + time.
// Returns "" when the row has no appointment date (those sort to the bottom).
const apptSortKey = (p) => {
  const date = String(p.appointmentDate || "").trim();
  if (!date) return "";
  const m = String(p.appointmentTime || "").match(
    /^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?/
  );
  let hh = 0;
  let mm = 0;
  if (m) {
    hh = parseInt(m[1], 10) % 24;
    mm = parseInt(m[2], 10) || 0;
    const ap = m[3] ? m[3].toUpperCase() : "";
    if (ap === "PM" && hh !== 12) hh += 12;
    if (ap === "AM" && hh === 12) hh = 0;
    // No AM/PM: same clinic-hours rule the reminders use (1-6 means PM).
    if (!ap && hh >= 1 && hh <= 6) hh += 12;
  }
  return `${date} ${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

// True when a row is missing any field the coordinator would need to fix, or
// has anything else worth a look (see reviewReasons). Drives the "Incomplete"
// filter. Display only — never changes what can be scheduled or sent.
const hasMissingDetails = (p) =>
  !p.phone ||
  p.sendable === false ||
  reviewReasons(p).length > 0;

// Review reasons that aren't just a blank field — the banner counts these
// separately so "needs a glance" isn't lost among "missing X" counts.
const MISSING_FIELD_REASONS = new Set([
  "Missing date",
  "Missing time",
  "Missing location",
  "Missing MRN",
]);

// True once the reminder cron has sent this row its 48h questionnaire text.
// Such rows now stay queued until the "forgot your questionnaire?" follow-up
// time, and are left out of Send / Send Selected so nobody gets the link twice.
// (Before the follow-up existed the cron deleted a row as soon as the 48h text
// went out, so Send could never reach it — this keeps that outcome. Legacy
// 7-day-only rows are deliberately NOT excluded: Send always reached those.)
const alreadyTexted = (p) =>
  Boolean(p && p.sends && p.sends.fortyEightHour && p.sends.fortyEightHour.sentAt);

// Rows the Schedule button will arm: a valid mobile, an appointment date, and
// not already armed.
const isSchedulable = (p) =>
  Boolean(p.phone) &&
  p.sendable !== false &&
  Boolean(p.appointmentDate) &&
  p.scheduled !== true;

// Rows shown per page in the pending list (display only — never affects which
// rows get scheduled or sent).
const PAGE_SIZE = 15;

const ReferralPage = () => {
  const navigate = useNavigate();

  const { isOwner, isTenantAdmin, roleForActiveSite, tenantId: activeTenantId } = useTenant();
  const isAdmin = isOwner || isTenantAdmin(activeTenantId) || roleForActiveSite === "admin";

  // Input row for adding a new number.
  const [newPhone, setNewPhone] = useState("");
  const [newName, setNewName] = useState("");
  const [newPatientName, setNewPatientName] = useState("");
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [newLocation, setNewLocation] = useState("");

  // Live data from Firestore (site-scoped).
  const [pending, setPending] = useState([]); // not-yet-sent working list
  // Latest pending rows for async handlers: a PDF extraction takes seconds, and
  // the reschedule check must compare against the queue as it is when saving.
  const pendingRef = useRef([]);
  const [recent, setRecent] = useState([]); // sent log ("Recently Referred")

  const [sending, setSending] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false); // Send confirmation modal
  // Which rows the confirm modal targets: "all" (every sendable row) or
  // "selected" (checkbox-selected scheduled rows only).
  const [sendScope, setSendScope] = useState("all");
  // Checkbox selection of SCHEDULED rows for a manual "send now" override.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Pending-list sort by appointment date. "desc" = latest appointment at the
  // top (default); the header arrow toggles direction.
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  // Display filters for the pending list — they never change what gets
  // scheduled or sent, only which rows are visible.
  const [filterIncomplete, setFilterIncomplete] = useState(false);
  const [filterNeedsSchedule, setFilterNeedsSchedule] = useState(false);
  const [filterDate, setFilterDate] = useState("all");
  const [filterCenter, setFilterCenter] = useState("all");
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null); // { sent, failed, results }
  const [scheduleNotice, setScheduleNotice] = useState(""); // "Armed N appointments…"

  // PDF appointment-schedule import.
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  // { total, sendable, duplicates, rescheduled }
  const [importSummary, setImportSummary] = useState(null);
  // Pages the reader couldn't handle on the last upload:
  // { pageCount, failedPages, emptyPages, skippedPages, maxPages } | null.
  const [importPageReport, setImportPageReport] = useState(null);

  // Inline edit of a pending row before send.
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({
    patientName: "",
    firstName: "",
    mrn: "",
    appointmentDate: "",
    appointmentTime: "",
    locationCode: "",
    mobilePhone: "",
  });

  // Subscribe to the temporary pending list — persists across refresh, and
  // the page re-mounts on a site switch (AppShell re-keys on tenant:site), so
  // this always reflects the ACTIVE site.
  useEffect(() => {
    // Site isolation: scope to the ACTIVE site (all referral docs share one pinned
    // path but carry their own siteId), so a site only ever sees its own numbers.
    const unsub = onSnapshot(
      query(
        collection(db, getReferralPendingPath()),
        where("siteId", "==", getActiveSiteId())
      ),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => millis(a.createdAt) - millis(b.createdAt));
        pendingRef.current = rows;
        setPending(rows);
      },
      (err) => console.error("Referral pending subscribe failed:", err)
    );
    return () => unsub();
  }, []);

  // Prune the checkbox selection whenever pending changes so ids of rows that
  // were sent/removed (here or by the cron) never linger in the selection.
  useEffect(() => {
    setSelectedIds((prev) => {
      const live = new Set(pending.map((p) => p.id));
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (live.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [pending]);

  // Subscribe to the sent log for "Recently Referred".
  useEffect(() => {
    const unsub = onSnapshot(
      query(
        collection(db, getReferralRecipientsPath()),
        where("siteId", "==", getActiveSiteId())
      ),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => millis(b.smsSentAt) - millis(a.smsSentAt));
        setRecent(rows.slice(0, 50));
      },
      (err) => console.error("Referral recipients subscribe failed:", err)
    );
    return () => unsub();
  }, []);

  const handleLogout = async () => {
    // logoutAndClearSite never throws, so the try/catch that used to wrap
    // signOut here lives inside it now.
    await logoutAndClearSite();
    navigate("/login");
  };

  // Add a number to the persistent pending list.
  const addPending = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError("");
    const phone = normalizePhone(newPhone);
    if (!phone) {
      setError("Enter a phone number first.");
      return;
    }
    const locationCode = newLocation.trim().toUpperCase();
    try {
      await addDoc(collection(db, getReferralPendingPath()), {
        // Site attribution: these collections share one pinned path, so each doc
        // carries the site it belongs to (the staff member's active site). It
        // flows through to the recipient and submission the scheduler creates.
        siteId: getActiveSiteId(),
        tenantId: getActiveTenantId(),
        phone,
        mobilePhone: phone,
        firstName: newName.trim() || "",
        patientName: newPatientName.trim() || "",
        appointmentDate: newDate.trim() || "",
        appointmentTime: newTime.trim() || "",
        locationCode,
        locationName: "",
        sendable: true,
        source: "manual",
        createdAt: serverTimestamp(),
      });
      setNewPhone("");
      setNewName("");
      setNewPatientName("");
      setNewDate("");
      setNewTime("");
      setNewLocation("");
    } catch (err) {
      console.error("Add pending failed:", err);
      setError("Couldn't save that number. Try again.");
    }
  };

  const removePending = async (id) => {
    try {
      await deleteDoc(doc(db, getReferralPendingPath(), id));
    } catch (err) {
      console.error("Remove pending failed:", err);
    }
  };

  const [deletingAll, setDeletingAll] = useState(false);
  const deleteAll = async () => {
    if (!pending.length) return;
    setDeletingAll(true);
    try {
      await Promise.all(
        pending.map((p) => deleteDoc(doc(db, getReferralPendingPath(), p.id)))
      );
    } catch (err) {
      console.error("Delete all pending failed:", err);
    } finally {
      setDeletingAll(false);
    }
  };

  // ---- PDF appointment-schedule import ------------------------------------
  // Upload a Cerner-style schedule PDF, extract appointment rows server-side,
  // and save them into the SAME referral_pending list the manual flow uses (so
  // refresh/send behavior is unchanged). Not-sendable rows are kept visible.
  const handlePickPdf = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset so the same file can be chosen again later.
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;

    setImportError("");
    setImportSummary(null);
    setImportPageReport(null);
    setImporting(true);
    try {
      const { rows, ...pageReport } = await extractReferralScheduleReport(file);
      // Always surface page problems — even when some rows came through, the
      // patients on an unreadable page were NOT added.
      if (
        pageReport.failedPages.length ||
        pageReport.emptyPages.length ||
        pageReport.skippedPages
      ) {
        setImportPageReport(pageReport);
      }
      if (!rows.length) {
        setImportError("No appointments were found in that PDF.");
        return;
      }
      let saved = 0;
      let savedSendable = 0;
      let duplicates = 0;
      let rescheduled = 0;
      // Match each row against the queue by MRN + patient name (see
      // referralRescheduleMatch). The same patient twice in THIS upload is two
      // slots on one schedule, so only their earliest appointment is kept.
      for (const r of collapseSamePatientInBatch(rows)) {
        const match = classifyImportedRow(r, pendingRef.current);

        // Already queued with this exact appointment (e.g. re-uploaded PDF).
        if (match.kind === "duplicate") {
          duplicates += 1;
          continue;
        }

        // Same patient, different appointment: the latest upload wins. The
        // queued row is updated IN PLACE, so it keeps its patient link and its
        // Schedule status — no staff step needed.
        if (match.kind === "reschedule") {
          const keep = pickRowToKeep(match.matches, (p) => millis(p.createdAt));
          try {
            await updateDoc(doc(db, getReferralPendingPath(), keep.id), {
              ...buildRescheduleFields(keep, r, new Date().toISOString()),
              // The old appointment's reminder state no longer applies: the new
              // appointment gets its own 48h reminder (sent right away if that
              // time has already passed), and a "send manually" flag raised
              // for the old one is re-evaluated by the cron.
              "sends.fortyEightHour": deleteField(),
              "sends.questionnaireNudge": deleteField(),
              needsManualSend: deleteField(),
            });
            // Any other queued rows for the same patient are stale copies.
            await Promise.all(
              match.matches
                .filter((m) => m.id !== keep.id)
                .map((m) => deleteDoc(doc(db, getReferralPendingPath(), m.id)))
            );
            rescheduled += 1;
          } catch (err) {
            console.error("Update rescheduled row failed:", err);
          }
          continue;
        }

        try {
          await addDoc(collection(db, getReferralPendingPath()), {
            // Site attribution (see addPending) — the active site this staffer
            // is importing under.
            siteId: getActiveSiteId(),
            tenantId: getActiveTenantId(),
            // Existing-compatible fields (used by the current send flow):
            phone: r.mobilePhone || "",
            firstName: r.firstName || "",
            // Extended appointment fields (ride along for review + future SMS):
            patientName: r.patientName || "",
            mrn: r.mrn || "",
            appointmentDate: r.appointmentDate || "",
            appointmentTime: r.appointmentTime || "",
            locationCode: r.locationCode || "",
            locationName: r.locationName || "",
            mobilePhone: r.mobilePhone || "",
            sendable: Boolean(r.sendable),
            sourcePage: r.sourcePage || 0,
            confidence: typeof r.confidence === "number" ? r.confidence : null,
            warnings: Array.isArray(r.warnings) ? r.warnings : [],
            source: "schedule_pdf",
            createdAt: serverTimestamp(),
          });
          saved += 1;
          if (r.sendable) savedSendable += 1;
        } catch (err) {
          console.error("Save imported row failed:", err);
        }
      }
      setImportSummary({
        total: saved,
        sendable: savedSendable,
        duplicates,
        rescheduled,
      });
    } catch (err) {
      console.error("Schedule import failed:", err);
      setImportError(err.message || "Couldn't read that schedule PDF.");
    } finally {
      setImporting(false);
    }
  };

  // ---- Inline edit of a pending row (mobile / name) before send -----------
  const startEdit = (row) => {
    setEditingId(row.id);
    setEditDraft({
      patientName: row.patientName || "",
      firstName: row.firstName || "",
      mrn: row.mrn || "",
      appointmentDate: row.appointmentDate || "",
      appointmentTime: row.appointmentTime || "",
      locationCode: row.locationCode || "",
      mobilePhone: row.mobilePhone || row.phone || "",
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ patientName: "", firstName: "", mrn: "", appointmentDate: "", appointmentTime: "", locationCode: "", mobilePhone: "" });
  };
  const saveEdit = async (id) => {
    const phone = toE164(editDraft.mobilePhone);
    const locationCode = editDraft.locationCode.trim().toUpperCase();
    try {
      await updateDoc(doc(db, getReferralPendingPath(), id), {
        phone,
        mobilePhone: phone,
        firstName: editDraft.firstName.trim(),
        patientName: editDraft.patientName.trim(),
        mrn: editDraft.mrn.trim(),
        appointmentDate: editDraft.appointmentDate.trim(),
        appointmentTime: editDraft.appointmentTime.trim(),
        locationCode,
        locationName: "",
        sendable: Boolean(phone),
        warnings: [],
      });
      cancelEdit();
    } catch (err) {
      console.error("Save edit failed:", err);
      setError("Couldn't save that change. Try again.");
    }
  };

  // Warning summary for imported rows with missing fields.
  const importWarnings = useMemo(() => {
    const apptRows = pending.filter((p) => p.source === "schedule_pdf" || p.source === "manual" || p.patientName || p.appointmentDate || p.locationCode);
    if (!apptRows.length) return null;
    const counts = {
      mobile: apptRows.filter((p) => !p.phone || p.sendable === false).length,
      location: apptRows.filter((p) => !p.locationCode).length,
      mrn: apptRows.filter((p) => p.source === "schedule_pdf" && !p.mrn).length,
      date: apptRows.filter((p) => !p.appointmentDate).length,
      time: apptRows.filter((p) => !p.appointmentTime).length,
    };
    // Rows with something other than a blank field to check (unknown location
    // code, AM/PM missing, low read confidence), and which kinds.
    const reviewKinds = new Set();
    const review = apptRows.filter((p) => {
      const extra = reviewReasons(p).filter((r) => !MISSING_FIELD_REASONS.has(r));
      extra.forEach((r) => reviewKinds.add(r.replace(/ — .*$/, "")));
      return extra.length > 0;
    }).length;
    const issues = [
      counts.mobile && `${counts.mobile} missing mobile`,
      counts.location && `${counts.location} missing location code`,
      counts.mrn && `${counts.mrn} missing MRN`,
      counts.date && `${counts.date} missing date`,
      counts.time && `${counts.time} missing time`,
      review && `${review} need review (${[...reviewKinds].join(", ").toLowerCase()})`,
    ].filter(Boolean);
    return issues.length ? issues : null;
  }, [pending]);

  // Rows that the Schedule button will arm: a valid mobile, a readable
  // appointment date (the cron needs it to compute the reminder windows), and
  // not already armed. Rows with no date can't be scheduled — they're manual
  // Send only.
  const schedulable = useMemo(
    () =>
      pending.filter(
        (p) => isSchedulable(p)
      ),
    [pending]
  );

  // How many rows the manual Send will actually text — same filter handleSend
  // uses. Drives the Send confirmation copy.
  const sendableCount = useMemo(
    () => pending.filter((p) => p.phone && p.sendable !== false && !alreadyTexted(p)).length,
    [pending]
  );

  // Scheduled (armed) rows that could be manually sent now. These are the only
  // rows that get a selection checkbox — Send Selected texts them immediately
  // and removes them from the reminder schedule.
  const scheduledSendable = useMemo(
    () =>
      pending.filter(
        (p) => p.scheduled === true && p.phone && p.sendable !== false && !alreadyTexted(p)
      ),
    [pending]
  );
  const selectedRows = useMemo(
    () => scheduledSendable.filter((p) => selectedIds.has(p.id)),
    [scheduledSendable, selectedIds]
  );
  const allScheduledSelected =
    scheduledSendable.length > 0 &&
    selectedRows.length === scheduledSendable.length;

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAllScheduled = () => {
    setSelectedIds(
      allScheduledSelected
        ? new Set()
        : new Set(scheduledSendable.map((p) => p.id))
    );
  };

  // Pending list ordered by appointment date/time. Rows without a date always
  // sink to the bottom (newest-added first among them) in either direction.
  const sortedPending = useMemo(() => {
    const dated = [];
    const undated = [];
    for (const p of pending) (apptSortKey(p) ? dated : undated).push(p);
    dated.sort((a, b) => {
      const cmp = apptSortKey(a).localeCompare(apptSortKey(b));
      return sortDir === "desc" ? -cmp : cmp;
    });
    undated.sort((a, b) => millis(b.createdAt) - millis(a.createdAt));
    return [...dated, ...undated];
  }, [pending, sortDir]);

  // Filter dropdown options, derived from whatever is currently in the list.
  const centerOptions = useMemo(() => {
    const set = new Set();
    for (const p of pending) {
      const c = String(p.locationCode || "").trim();
      if (c) set.add(c);
    }
    return [...set].sort();
  }, [pending]);

  // Display filters only — Schedule / Send / select-all still act on the full
  // list regardless of what's filtered out here.
  const filteredPending = useMemo(
    () =>
      sortedPending.filter((p) => {
        if (filterIncomplete && !hasMissingDetails(p)) return false;
        if (filterNeedsSchedule && !isSchedulable(p)) return false;
        if (filterDate === "none") {
          if (String(p.appointmentDate || "").trim()) return false;
        } else if (filterDate !== "all") {
          if (String(p.appointmentDate || "").trim() !== filterDate) return false;
        }
        if (filterCenter === "none") {
          if (String(p.locationCode || "").trim()) return false;
        } else if (filterCenter !== "all") {
          if (String(p.locationCode || "").trim() !== filterCenter) return false;
        }
        return true;
      }),
    [sortedPending, filterIncomplete, filterNeedsSchedule, filterDate, filterCenter]
  );
  const filtersActive =
    filterIncomplete ||
    filterNeedsSchedule ||
    filterDate !== "all" ||
    filterCenter !== "all";

  // Pagination — display only. Every action (schedule, send, select-all) still
  // operates on the full list, not just the visible page.
  const pageCount = Math.max(1, Math.ceil(filteredPending.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pagedPending = filteredPending.slice(pageStart, pageStart + PAGE_SIZE);

  // Keep the page in range as rows are added/removed or the sort flips.
  useEffect(() => {
    setPage((p) => Math.min(p, pageCount));
  }, [pageCount]);
  useEffect(() => {
    setPage(1);
  }, [sortDir, filterIncomplete, filterNeedsSchedule, filterDate, filterCenter]);

  // Arm every eligible dated row: the reminder cron then owns them (one text,
  // 48h before the appointment). Nothing is sent now — this only hands the
  // rows to the scheduler. Send remains the manual override.
  const handleSchedule = async () => {
    setError("");
    setSummary(null);
    setScheduleNotice("");

    if (schedulable.length === 0) {
      setError(
        "Nothing to schedule. Rows need a valid mobile and an appointment date; " +
          "use Send for anything without a date."
      );
      return;
    }

    setScheduling(true);
    try {
      await Promise.all(
        schedulable.map((p) =>
          updateDoc(doc(db, getReferralPendingPath(), p.id), {
            scheduled: true,
            scheduledAt: serverTimestamp(),
          })
        )
      );
      const n = schedulable.length;
      setScheduleNotice(
        `Scheduled ${n} appointment${n === 1 ? "" : "s"}. A reminder will send automatically ` +
          `48 hours before each appointment, plus a follow-up shortly before the visit ` +
          `for anyone who hasn't filled out the questionnaire.`
      );
    } catch (err) {
      console.error("Schedule failed:", err);
      setError(err.message || "Could not schedule these appointments.");
    } finally {
      setScheduling(false);
    }
  };

  const handleSend = async (scope = "all") => {
    setError("");
    setSummary(null);
    setScheduleNotice("");

    if (pending.length === 0) {
      setError("Add at least one phone number first.");
      return;
    }

    // Only rows with a valid mobile are sent. Not-sendable imported rows stay
    // visible in the pending list (nothing is dropped) so they can be fixed.
    // "selected" scope: only the checkbox-selected scheduled rows — a manual
    // "send now" that also takes them off the reminder schedule.
    const sendables =
      scope === "selected"
        ? scheduledSendable.filter((p) => selectedIds.has(p.id))
        : pending.filter((p) => p.phone && p.sendable !== false && !alreadyTexted(p));
    if (sendables.length === 0) {
      setError(
        scope === "selected"
          ? "No scheduled patients are selected."
          : "No rows have a valid mobile number to send to."
      );
      return;
    }

    setSending(true);
    try {
      // One recipient per sendable pending doc. REUSE the row's token/link when
      // the reminder cron already minted one (so the patient keeps a single
      // working link and "already submitted" sticks); mint fresh otherwise.
      // Keep the pending id so we can move it after a confirmed send.
      // Appointment fields ride along for the recipient log / SMS template.
      const recipients = sendables.map((p) => {
        const token = p.token || makeToken();
        return {
          id: p.id,
          recipientId: p.recipientId || "",
          // Carry the pending row's site so the recipient (and, in turn, the
          // patient's submission) belongs to the same site as the number sent.
          siteId: p.siteId || getActiveSiteId(),
          tenantId: p.tenantId || getActiveTenantId(),
          phone: p.phone,
          firstName: p.firstName || "",
          token,
          // The site rides in the link so the unauthenticated questionnaire page
          // knows which site to read the recipient from and write the submission
          // to. Reuse an existing (cron-minted) link if present.
          link: p.link || `${REFERRAL_LINK_BASE}/${p.siteId || getActiveSiteId()}/${token}`,
          appointment: {
            patientName: p.patientName || "",
            mrn: p.mrn || "",
            appointmentDate: p.appointmentDate || "",
            appointmentTime: p.appointmentTime || "",
            locationCode: p.locationCode || "",
            locationName: p.locationName || "",
            sourcePage: p.sourcePage || 0,
          },
        };
      });

      const res = await authedFetch(SEND_MASS_REFERRAL_SMS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recipients: recipients.map(({ phone, link, firstName, appointment }) => ({
            phone,
            link,
            firstName,
            appointmentDate: appointment.appointmentDate,
            appointmentTime: appointment.appointmentTime,
            locationName: appointment.locationName,
            locationCode: appointment.locationCode,
          })),
          // The site sending the texts — the server checks membership against
          // this rather than the deployment's own site.
          tenantId: getActiveTenantId(),
          siteId: getActiveSiteId(),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Mass text failed to send.");
      }

      // The SMS gateway result is the source of truth. Move ONLY confirmed
      // sends into "Recently Referred" and out of pending; leave any failures
      // in the pending list so they can be retried (nothing is lost).
      const outcomeByPhone = new Map(
        (data.results || []).map((x) => [x.phone, x.success])
      );
      await Promise.all(
        recipients.map(async (r) => {
          if (!outcomeByPhone.get(r.phone)) return; // failed → keep in pending
          try {
            // Write to the log FIRST, then delete from pending — so a hiccup
            // can never drop a number from both places. Rows the reminder cron
            // already texted have a recipient doc — stamp it instead of
            // creating a duplicate log entry.
            let logged = false;
            let recipientId = r.recipientId || "";
            if (r.recipientId) {
              try {
                await updateDoc(
                  doc(db, getReferralRecipientsPath(), r.recipientId),
                  { status: "sent", smsSentAt: serverTimestamp() }
                );
                logged = true;
              } catch (updErr) {
                console.warn(
                  "Existing recipient update failed — logging fresh:",
                  updErr
                );
              }
            }
            if (!logged) {
              const recRef = await addDoc(
                collection(db, getReferralRecipientsPath()),
                {
                  // Site attribution — the recipient belongs to the sending
                  // staffer's site, so the patient's submission lands there too.
                  siteId: r.siteId,
                  tenantId: r.tenantId,
                  phone: r.phone,
                  firstName: r.firstName,
                  token: r.token,
                  link: r.link,
                  status: "sent",
                  // Appointment fields passed through from the imported schedule
                  // (empty strings for manually-added numbers). NOTE: the MRN is
                  // intentionally NOT stored on this public, token-readable doc —
                  // it goes to the private subcollection below (PHI).
                  patientName: r.appointment.patientName,
                  appointmentDate: r.appointment.appointmentDate,
                  appointmentTime: r.appointment.appointmentTime,
                  locationCode: r.appointment.locationCode,
                  locationName: r.appointment.locationName,
                  sourcePage: r.appointment.sourcePage,
                  createdAt: serverTimestamp(),
                  smsSentAt: serverTimestamp(),
                }
              );
              recipientId = recRef.id;
            }

            // Store the MRN in the staff-only private subcollection (never on the
            // public recipient doc). The patient's browser never reads this.
            if (recipientId && r.appointment.mrn) {
              try {
                await setDoc(
                  doc(db, getReferralRecipientMrnDocPath(recipientId)),
                  { mrn: r.appointment.mrn, updatedAt: serverTimestamp() },
                  { merge: true }
                );
              } catch (mrnErr) {
                console.warn("Could not store private recipient MRN:", mrnErr);
              }
            }
            // Deleting the pending doc also un-schedules the row — the
            // reminder cron only reads referral_pending.
            await deleteDoc(doc(db, getReferralPendingPath(), r.id));
          } catch (moveErr) {
            console.error(
              "Move to Recently Referred failed (kept in pending):",
              moveErr
            );
          }
        })
      );

      setSummary({
        sent: data.sent ?? 0,
        failed: data.failed ?? 0,
        results: data.results || [],
      });
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong sending the mass text.");
    } finally {
      setSending(false);
    }
  };

  // Render one pending row: inline-edit mode, a compact manual row, or a rich
  // imported-appointment card. Nothing here changes the send payload shape.
  const renderPendingRow = (row) => {
    const isImported = Boolean(
      row.patientName || row.source === "schedule_pdf" || row.source === "manual" || row.mrn || row.appointmentDate || row.locationCode
    );
    const notSendable = row.sendable === false || !row.phone;
    const warnings = Array.isArray(row.warnings) ? row.warnings : [];

    if (editingId === row.id) {
      const missingCls = "border-amber-400/40";
      const normalCls = "border-white/10";
      const baseCls = "w-full px-2.5 py-1.5 rounded-lg bg-white/[0.04] text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 border";
      const isImported = Boolean(row.source === "schedule_pdf" || row.patientName || row.mrn);

      return (
        <div
          key={row.id}
          className="rounded-xl bg-white/[0.03] border border-purple-400/30 p-3 space-y-2 md:col-span-2 xl:col-span-3"
        >
          {/* Name row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Patient Name</p>
              <input
                value={editDraft.patientName}
                onChange={(e) => setEditDraft({ ...editDraft, patientName: e.target.value })}
                placeholder="Patient name"
                className={`${baseCls} ${!editDraft.patientName ? missingCls : normalCls}`}
              />
            </div>
            <div>
              <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">First Name</p>
              <input
                value={editDraft.firstName}
                onChange={(e) => setEditDraft({ ...editDraft, firstName: e.target.value })}
                placeholder="First name"
                className={`${baseCls} ${!editDraft.firstName ? missingCls : normalCls}`}
              />
            </div>
          </div>

          {/* Appointment details row — only for imported rows */}
          {isImported && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">MRN</p>
                <input
                  value={editDraft.mrn}
                  onChange={(e) => setEditDraft({ ...editDraft, mrn: e.target.value })}
                  placeholder="MRN"
                  className={`${baseCls} font-mono ${!editDraft.mrn ? missingCls : normalCls}`}
                />
              </div>
              <div>
                <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Date</p>
                <input
                  value={editDraft.appointmentDate}
                  onChange={(e) => setEditDraft({ ...editDraft, appointmentDate: e.target.value })}
                  placeholder="YYYY-MM-DD"
                  className={`${baseCls} ${!editDraft.appointmentDate ? missingCls : normalCls}`}
                />
              </div>
              <div>
                <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Time</p>
                <input
                  value={editDraft.appointmentTime}
                  onChange={(e) => setEditDraft({ ...editDraft, appointmentTime: e.target.value })}
                  placeholder="9:00 AM"
                  className={`${baseCls} ${!editDraft.appointmentTime ? missingCls : normalCls}`}
                />
              </div>
            </div>
          )}

          {/* Location + mobile row */}
          <div className={`grid grid-cols-1 gap-2 ${isImported ? "sm:grid-cols-2" : ""}`}>
            {isImported && (
              <div>
                <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Location Code</p>
                <input
                  value={editDraft.locationCode}
                  onChange={(e) => setEditDraft({ ...editDraft, locationCode: e.target.value })}
                  placeholder="e.g. LUNG PHILIPS"
                  className={`${baseCls} uppercase ${!editDraft.locationCode ? missingCls : normalCls}`}
                />
              </div>
            )}
            <div>
              <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Mobile Phone</p>
              <input
                value={editDraft.mobilePhone}
                onChange={(e) => setEditDraft({ ...editDraft, mobilePhone: e.target.value })}
                placeholder="Mobile (10-digit)"
                className={`${baseCls} font-mono ${!editDraft.mobilePhone ? missingCls : normalCls}`}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => saveEdit(row.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 text-sm hover:bg-emerald-500/25 transition"
            >
              <Check className="w-4 h-4" /> Save
            </button>
            <button
              onClick={cancelEdit}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-300 text-sm hover:bg-white/[0.1] transition"
            >
              <X className="w-4 h-4" /> Cancel
            </button>
            <span className="text-[11px] text-slate-500">
              Amber borders indicate missing fields. A blank mobile keeps the row unsent.
            </span>
          </div>
        </div>
      );
    }

    if (!isImported) {
      // Manual row — original compact rendering (+ edit affordance).
      return (
        <div
          key={row.id}
          className="min-h-[7.5rem] flex items-center gap-3 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/10"
        >
          <Phone className="w-4 h-4 text-purple-300/70 shrink-0" />
          <span className="font-mono text-sm text-slate-100">{row.phone}</span>
          {row.firstName && (
            <span className="text-sm text-slate-400">· {row.firstName}</span>
          )}
          <span className="flex-1" />
          <button
            onClick={() => startEdit(row)}
            title="Edit"
            className="p-1.5 rounded-lg text-slate-400 hover:text-purple-300 transition"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => removePending(row.id)}
            title="Remove"
            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-300 transition"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      );
    }

    // Imported appointment row.
    // Anything worth a human glance (missing field, unknown location code,
    // AM/PM missing, low read confidence). Label only — Schedule and Send
    // still work on these rows exactly as before.
    const reasons = notSendable ? [] : reviewReasons(row);
    const needsReview = reasons.length > 0;

    // The reminder cron sets needsManualSend when no reminder window will ever
    // fire for this row — no appointment date, or the appointment was under 2
    // hours away (or past) by the time it was scheduled. These rows are NOT dropped; they
    // sit here until a human sends them.
    const needsManualSend = !notSendable && row.needsManualSend === true;

    // An ARMED dated row is owned by the reminder cron.
    const isScheduled = !notSendable && !needsManualSend && row.scheduled === true;
    // Armed and already sent the 48h text — only the follow-up is left.
    const awaitingFollowUp = isScheduled && alreadyTexted(row);

    // A dated row that isn't armed yet — the Schedule button will pick it up.
    const canSchedule =
      !notSendable && !needsManualSend && Boolean(row.appointmentDate) && row.scheduled !== true;

    // Set when a later upload moved this patient's appointment (informational).
    const movedFrom = row.rescheduledFrom || null;

    const cardBorder = notSendable
      ? "border-rose-400/40"
      : needsManualSend
      ? "border-amber-400/40"
      : isScheduled
      ? "border-sky-400/40"
      : needsReview
      ? "border-yellow-400/40"
      : "border-white/10";

    return (
      <div
        key={row.id}
        className={`min-h-[7.5rem] rounded-xl bg-white/[0.03] border p-3 ${cardBorder}`}
      >
        <div className="flex items-start gap-3">
          {((isScheduled && !awaitingFollowUp) || needsManualSend) && (
            <input
              type="checkbox"
              checked={selectedIds.has(row.id)}
              onChange={() => toggleSelected(row.id)}
              title={
                needsManualSend
                  ? "Select to send now — no automatic reminder will fire for this row"
                  : "Select to send now (removes from the reminder schedule)"
              }
              className={`mt-1 w-4 h-4 shrink-0 cursor-pointer ${
                needsManualSend ? "accent-amber-400" : "accent-sky-400"
              }`}
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-white truncate">
                {row.patientName || "(no name)"}
              </span>
              {notSendable ? (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-rose-400/15 text-rose-200 border border-rose-400/30">
                  <AlertTriangle className="w-3 h-3" /> No phone — not sendable
                </span>
              ) : needsManualSend ? (
                <span
                  title="No reminder will send automatically for this row — either it has no appointment date, or the appointment was less than 2 hours away (or already past) when it was scheduled. Use Send to text them now."
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-amber-400/15 text-amber-200 border border-amber-400/30"
                >
                  <AlertTriangle className="w-3 h-3" /> Send manually — no reminder scheduled
                </span>
              ) : awaitingFollowUp ? (
                <span
                  title="The 48-hour questionnaire text went out. If they haven't filled it out, a follow-up text goes out 3 hours before the appointment (or 6:00 PM ET the evening before, for appointments before 10 AM). Filling it out cancels the follow-up."
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-sky-400/15 text-sky-200 border border-sky-400/30"
                >
                  <CheckCircle2 className="w-3 h-3" /> Texted — follow-up if not filled out
                </span>
              ) : isScheduled ? (
                <span
                  title="Armed. A reminder sends automatically 48 hours before the appointment (8:00 AM ET two days before if there's no time) — or right away if that time has already passed, as long as the appointment is more than 2 hours off. If the questionnaire still isn't filled out, a follow-up goes out 3 hours before the visit (6:00 PM ET the evening before for appointments before 10 AM). Filling out the form cancels any remaining texts."
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-sky-400/15 text-sky-200 border border-sky-400/30"
                >
                  <Clock className="w-3 h-3" /> Reminders scheduled
                </span>
              ) : needsReview ? (
                <span
                  title={`Check before scheduling: ${reasons.join("; ")}. Click the pencil to fix. ${
                    canSchedule
                      ? "Schedule and Send still work on this row."
                      : "Send still works on this row; Schedule needs an appointment date."
                  }`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-yellow-400/15 text-yellow-200 border border-yellow-400/30"
                >
                  <AlertTriangle className="w-3 h-3" /> Needs review — {reasons[0]}
                  {reasons.length > 1 ? ` +${reasons.length - 1}` : ""}
                </span>
              ) : canSchedule ? (
                <span
                  title="Has an appointment date. Click Schedule to arm automatic reminders, or Send to text now."
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-emerald-400/15 text-emerald-200 border border-emerald-400/30"
                >
                  <CalendarClock className="w-3 h-3" /> Ready to schedule
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-emerald-400/15 text-emerald-200 border border-emerald-400/30">
                  <CheckCircle2 className="w-3 h-3" /> Ready
                </span>
              )}
              {movedFrom && (
                <span
                  title={`Updated from a newer schedule upload. Previously: ${
                    [movedFrom.appointmentDate, movedFrom.appointmentTime, movedFrom.locationCode]
                      .filter(Boolean)
                      .join(" · ") || "no appointment details"
                  }`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-violet-400/15 text-violet-200 border border-violet-400/30"
                >
                  <CalendarClock className="w-3 h-3" /> Rescheduled
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-slate-400">
              {row.mrn && (
                <span className="inline-flex items-center gap-1">
                  <Hash className="w-3 h-3" />
                  {row.mrn}
                </span>
              )}
              {row.appointmentDate && (
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {row.appointmentDate}
                </span>
              )}
              {row.appointmentTime && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {row.appointmentTime}
                </span>
              )}
            </div>
            {/* Center always gets its own line so cards stay consistent. */}
            {(row.locationName || row.locationCode) && (
              <div className="mt-1 flex items-center gap-1 text-xs text-slate-400">
                <MapPin className="w-3 h-3 shrink-0" />
                <span className="truncate">
                  {row.locationName || row.locationCode}
                </span>
              </div>
            )}
            <div className="mt-1 flex items-center gap-2 text-sm">
              <Phone className="w-3.5 h-3.5 text-purple-300/70 shrink-0" />
              {row.phone ? (
                <span className="font-mono text-slate-100">{row.phone}</span>
              ) : (
                <span className="text-amber-300/80">No mobile number</span>
              )}
            </div>
            {warnings.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {warnings.map((w, i) => (
                  <span
                    key={i}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-200/90 border border-amber-400/20"
                  >
                    {w}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => startEdit(row)}
              title="Edit"
              className="p-1.5 rounded-lg text-slate-400 hover:text-purple-300 transition"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={() => removePending(row.id)}
              title="Remove"
              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-300 transition"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="referral-page relative min-h-screen w-full text-slate-100 font-[Manrope]">
      {/* Background */}
      <div className="referral-bg fixed inset-0 -z-10 overflow-hidden bg-[#070418]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.22),_transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(37,99,235,0.18),_transparent_55%)]" />
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300/80 font-semibold">
              Lung Innovations · Referral
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-white">
              Referral Outreach
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.05] border border-white/10 text-sm text-slate-200 hover:bg-white/[0.08] transition"
              >
                <LayoutDashboard className="w-4 h-4 text-purple-300" />
                Admin
              </button>
            )}
            <button
              onClick={() => navigate("/coordinator")}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.05] border border-white/10 text-sm text-slate-200 hover:bg-white/[0.08] transition"
            >
              <Users className="w-4 h-4 text-purple-300" />
              Coordinator Dashboard
            </button>
            <button
              onClick={() => navigate("/studies")}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.05] border border-white/10 text-sm text-slate-200 hover:bg-white/[0.08] transition"
            >
              <Library className="w-4 h-4 text-purple-300" />
              Study Library
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.05] border border-white/10 text-sm text-slate-300 hover:bg-white/[0.08] transition"
            >
              <LogOut className="w-4 h-4" />
              Log Out
            </button>
          </div>
        </div>

        {/* Card — add numbers + pending list */}
        <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_20px_60px_-20px_rgba(124,58,237,0.45)] overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-70" />
          <div className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-[10px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold">
                Phone Numbers
              </span>
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-xs text-slate-400">
                {pending.length} pending
              </span>
              {pending.length > 0 && (
                <button
                  type="button"
                  onClick={deleteAll}
                  disabled={deletingAll}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-rose-500/10 border border-rose-400/20 text-xs text-rose-300 hover:bg-rose-500/20 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {deletingAll ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3" />
                  )}
                  Delete All
                </button>
              )}
            </div>

            {importWarnings && (
              <div className="mb-3 rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2.5 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm text-amber-200 font-medium">Some patients have incomplete data</p>
                  <p className="text-xs text-amber-300/80 mt-0.5">{importWarnings.join(" · ")} — click the pencil icon to fix</p>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-3 rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-rose-300 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Import appointment schedule PDF */}
            <div className="mb-4 rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <div className="flex flex-col items-center justify-center gap-2 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={handlePickPdf}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() =>
                    fileInputRef.current && fileInputRef.current.click()
                  }
                  disabled={importing}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.06] border border-white/10 text-sm text-slate-100 hover:bg-white/[0.1] transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Extracting…
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 text-purple-300" />
                      Extract Schedule (PDF)
                    </>
                  )}
                </button>
                <span className="text-xs text-slate-500 flex items-center justify-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 shrink-0" />
                  Upload an appointment schedule — rows are added below for review.
                </span>
              </div>
              {importError && (
                <div className="mt-2 text-xs text-rose-300 flex items-center justify-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  {importError}
                </div>
              )}
              {importSummary && (
                <div className="mt-2 text-xs text-emerald-300 flex items-center justify-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                  Imported {importSummary.total} appointment
                  {importSummary.total === 1 ? "" : "s"} · {importSummary.sendable}{" "}
                  with a valid mobile.
                  {importSummary.duplicates > 0 &&
                    ` ${importSummary.duplicates} already queued — skipped.`}
                </div>
              )}
              {importSummary && importSummary.rescheduled > 0 && (
                <div className="mt-1 text-xs text-violet-300 flex items-center justify-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5 shrink-0" />
                  {importSummary.rescheduled} already-queued patient
                  {importSummary.rescheduled === 1 ? "" : "s"} updated to the new appointment.
                </div>
              )}
              {importPageReport && (
                <div className="mt-2 rounded-lg border border-white/10 bg-black/10 px-3 py-2 text-xs space-y-1 text-left relative">
                  <button
                    type="button"
                    onClick={() => setImportPageReport(null)}
                    title="Dismiss"
                    className="absolute top-1.5 right-1.5 p-0.5 rounded text-slate-400 hover:text-slate-200"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  {importPageReport.failedPages.length > 0 && (
                    <div className="flex items-start gap-1.5 text-rose-300 pr-5">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>
                        Couldn't read page{importPageReport.failedPages.length === 1 ? "" : "s"}{" "}
                        {importPageReport.failedPages.join(", ")} of {importPageReport.pageCount}
                        {" "}— patients on {importPageReport.failedPages.length === 1 ? "that page were" : "those pages were"}{" "}
                        <strong>not added</strong>. Upload the PDF again; patients already in the
                        list are skipped automatically.
                      </span>
                    </div>
                  )}
                  {importPageReport.skippedPages > 0 && (
                    <div className="flex items-start gap-1.5 text-rose-300 pr-5">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>
                        This PDF has {importPageReport.pageCount} pages; only the first{" "}
                        {importPageReport.maxPages || importPageReport.pageCount - importPageReport.skippedPages}{" "}
                        were read. Split it and upload the remaining pages.
                      </span>
                    </div>
                  )}
                  {importPageReport.emptyPages.length > 0 && (
                    <div className="flex items-start gap-1.5 text-amber-300 pr-5">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>
                        No appointments found on page{importPageReport.emptyPages.length === 1 ? "" : "s"}{" "}
                        {importPageReport.emptyPages.join(", ")} — check{" "}
                        {importPageReport.emptyPages.length === 1 ? "it" : "them"} if there should be
                        patients there.
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Add row */}
            <form onSubmit={addPending} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2">
              {/* All six fields on one row once there's room. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2">
                <div>
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Mobile Phone *</p>
                  <div className="flex items-stretch rounded-lg border border-white/10 bg-white/[0.04] focus-within:border-purple-400/50 focus-within:ring-2 focus-within:ring-purple-500/20 transition overflow-hidden">
                    <div className="flex items-center gap-1 pl-2.5 pr-2 border-r border-white/10 shrink-0">
                      <Phone className="w-3.5 h-3.5 text-slate-500" />
                      <span className="text-xs text-slate-400 font-medium select-none">+1</span>
                    </div>
                    <input
                      type="tel"
                      inputMode="tel"
                      placeholder="(555) 123-4567"
                      className="flex-1 min-w-0 px-2.5 py-1.5 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">First Name</p>
                  <input
                    type="text"
                    placeholder="First name"
                    className="w-full px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 transition"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </div>
                <div>
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Patient Name</p>
                  <input
                    type="text"
                    placeholder="Full name"
                    className="w-full px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 transition"
                    value={newPatientName}
                    onChange={(e) => setNewPatientName(e.target.value)}
                  />
                </div>
                <div>
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Appointment Date</p>
                  <input
                    type="text"
                    placeholder="YYYY-MM-DD"
                    className="w-full px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 transition"
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                  />
                </div>
                <div>
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Appointment Time</p>
                  <input
                    type="text"
                    placeholder="9:00 AM"
                    className="w-full px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 transition"
                    value={newTime}
                    onChange={(e) => setNewTime(e.target.value)}
                  />
                </div>
                <div>
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Location Code</p>
                  <input
                    type="text"
                    placeholder="e.g. LUNG PHILIPS"
                    className="w-full px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 transition uppercase"
                    value={newLocation}
                    onChange={(e) => setNewLocation(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.06] border border-white/10 text-sm text-slate-100 hover:bg-white/[0.1] transition"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
            </form>

            {/* Select-all for scheduled rows (manual send-now override) */}
            {scheduledSendable.length > 0 && (
              <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-xl bg-sky-500/[0.06] border border-sky-400/20">
                <input
                  id="select-all-scheduled"
                  type="checkbox"
                  checked={allScheduledSelected}
                  onChange={toggleSelectAllScheduled}
                  className="w-4 h-4 shrink-0 accent-sky-400 cursor-pointer"
                />
                <label
                  htmlFor="select-all-scheduled"
                  className="text-xs text-sky-200/90 cursor-pointer select-none"
                >
                  Select all scheduled ({scheduledSendable.length})
                </label>
                {selectedRows.length > 0 && (
                  <span className="ml-auto text-xs text-sky-300/80">
                    {selectedRows.length} selected — Send Selected texts them now
                    and unschedules their reminders.
                  </span>
                )}
              </div>
            )}

            {/* Filters — narrow the view only; nothing is scheduled or sent
                based on what's filtered here. Single row: the bar scrolls
                sideways rather than wrapping, so the sort button stays right. */}
            {pending.length > 1 && (
              <div className="mt-4 flex items-center gap-2 flex-nowrap overflow-x-auto px-3 py-2 rounded-xl bg-white/[0.02] border border-white/10">
                <Filter className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                <button
                  type="button"
                  onClick={() => setFilterIncomplete((v) => !v)}
                  title="Show only rows missing a mobile, location code, MRN, date or time."
                  className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs transition ${
                    filterIncomplete
                      ? "bg-amber-500/20 border-amber-400/40 text-amber-100"
                      : "bg-white/[0.05] border-white/10 text-slate-300 hover:bg-white/[0.1]"
                  }`}
                >
                  <AlertTriangle className="w-3 h-3" />
                  Missing details
                </button>

                <button
                  type="button"
                  onClick={() => setFilterNeedsSchedule((v) => !v)}
                  title="Show only rows the Schedule button will arm — a valid mobile and appointment date, not yet scheduled."
                  className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs transition ${
                    filterNeedsSchedule
                      ? "bg-sky-500/20 border-sky-400/40 text-sky-100"
                      : "bg-white/[0.05] border-white/10 text-slate-300 hover:bg-white/[0.1]"
                  }`}
                >
                  <CalendarClock className="w-3 h-3" />
                  Needs scheduling
                </button>

                {/* Appointment date — one control: pick a day, or "No date". */}
                <div className="shrink-0 flex items-stretch rounded-lg bg-white/[0.05] border border-white/10 overflow-hidden">
                  <div className="flex items-center gap-1.5 px-2 py-1">
                    <Calendar className="w-3 h-3 text-slate-500 shrink-0" />
                    <input
                      type="date"
                      title="Show only appointments on this date."
                      value={
                        filterDate === "all" || filterDate === "none"
                          ? ""
                          : filterDate
                      }
                      onChange={(e) => setFilterDate(e.target.value || "all")}
                      className="bg-transparent text-xs text-slate-300 focus:outline-none [color-scheme:dark]"
                    />
                    {filterDate !== "all" && filterDate !== "none" && (
                      <button
                        type="button"
                        onClick={() => setFilterDate("all")}
                        title="Clear the date filter"
                        className="text-slate-400 hover:text-slate-200 transition"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setFilterDate((d) => (d === "none" ? "all" : "none"))
                    }
                    title="Show only rows that have no appointment date."
                    className={`px-2.5 border-l border-white/10 text-xs transition ${
                      filterDate === "none"
                        ? "bg-amber-500/20 text-amber-100"
                        : "text-slate-300 hover:bg-white/[0.08]"
                    }`}
                  >
                    No date
                  </button>
                </div>

                <select
                  value={filterCenter}
                  onChange={(e) => setFilterCenter(e.target.value)}
                  className="shrink-0 px-2.5 py-1 rounded-lg bg-white/[0.05] border border-white/10 text-xs text-slate-300 focus:outline-none focus:border-purple-400/50 transition uppercase"
                >
                  <option value="all">All centers</option>
                  <option value="none">No center</option>
                  {centerOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>

                {/* Right-hand group — kept together so the sort button never
                    wraps to its own line when the filter count appears. */}
                <div className="ml-auto flex items-center gap-2 shrink-0">
                  {filtersActive && (
                    <span className="flex items-center gap-2 text-xs text-slate-400 whitespace-nowrap">
                      <span>
                        {filteredPending.length} of {pending.length} shown
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setFilterIncomplete(false);
                          setFilterNeedsSchedule(false);
                          setFilterDate("all");
                          setFilterCenter("all");
                        }}
                        className="px-2 py-1 rounded-lg bg-white/[0.05] border border-white/10 text-slate-300 hover:bg-white/[0.1] transition"
                      >
                        Clear
                      </button>
                    </span>
                  )}

                  {/* Appointment date sort — display order only. */}
                  <button
                    type="button"
                    onClick={() =>
                      setSortDir((d) => (d === "desc" ? "asc" : "desc"))
                    }
                    title={
                      sortDir === "desc"
                        ? "Sorted by appointment date: latest first — click for earliest first"
                        : "Sorted by appointment date: earliest first — click for latest first"
                    }
                    aria-label="Toggle appointment date sort order"
                    className="shrink-0 p-1.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-300 hover:bg-white/[0.1] transition"
                  >
                    <ArrowUpDown className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* Pending list — 3-up grid on wide screens, stacking on narrow. */}
            {/* items-stretch equalizes cards within a row; the min-height on
                each card equalizes them across rows, so a card with fewer
                details doesn't render shorter than the rest. */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 items-stretch">
              {pending.length === 0 ? (
                <p className="col-span-full text-sm text-slate-500 py-4 text-center">
                  No numbers yet. Add one above or import a schedule PDF — your
                  list is saved and stays here until you send it.
                </p>
              ) : filteredPending.length === 0 ? (
                <p className="col-span-full text-sm text-slate-500 py-4 text-center">
                  No patients match these filters. Clear them to see the full
                  list — everyone is still scheduled and will be sent.
                </p>
              ) : (
                pagedPending.map((row) => renderPendingRow(row))
              )}
            </div>

            {/* Pager — display only; Schedule/Send still cover the whole list. */}
            {pageCount > 1 && (
              <div className="mt-3 flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-white/[0.02] border border-white/10">
                <span className="text-xs text-slate-400">
                  Showing {pageStart + 1}–{pageStart + pagedPending.length} of{" "}
                  {filteredPending.length}
                  {filtersActive ? " filtered" : ""}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/[0.05] border border-white/10 text-xs text-slate-300 hover:bg-white/[0.1] transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-3 h-3" />
                    Prev
                  </button>
                  <span className="text-xs text-slate-400 px-1 tabular-nums">
                    Page {safePage} of {pageCount}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    disabled={safePage === pageCount}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/[0.05] border border-white/10 text-xs text-slate-300 hover:bg-white/[0.1] transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {/* Schedule confirmation */}
            {scheduleNotice && (
              <div className="mt-4 rounded-xl border border-sky-400/30 bg-sky-500/10 px-3 py-2.5 text-sm text-sky-100 flex items-start gap-2">
                <CalendarCheck className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{scheduleNotice}</span>
              </div>
            )}

            {/* Schedule + Send */}
            <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-400 leading-relaxed max-w-sm">
                <strong className="text-slate-200">Schedule</strong> arms automatic
                reminders for dated appointments (48 hours before, plus a follow-up
                if the questionnaire isn't filled out).{" "}
                <strong className="text-slate-200">Send</strong> texts everyone now and
                moves them to{" "}
                <strong className="text-slate-200">Recently Referred</strong>.
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={handleSchedule}
                  disabled={scheduling || sending || schedulable.length === 0}
                  title={
                    schedulable.length === 0
                      ? "No unscheduled dated appointments to arm."
                      : `Arm reminders for ${schedulable.length} appointment${schedulable.length === 1 ? "" : "s"}.`
                  }
                  className="py-2.5 px-4 rounded-xl bg-white/[0.06] border border-sky-400/30 text-sky-100 text-sm font-semibold hover:bg-sky-500/10 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center justify-center gap-2">
                    {scheduling ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Scheduling…
                      </>
                    ) : (
                      <>
                        <CalendarClock className="w-4 h-4" />
                        Schedule{schedulable.length ? ` (${schedulable.length})` : ""}
                      </>
                    )}
                  </span>
                </button>
                {scheduledSendable.length > 0 && (
                  <button
                    onClick={() => {
                      setSendScope("selected");
                      setConfirmSend(true);
                    }}
                    disabled={sending || scheduling || selectedRows.length === 0}
                    title={
                      selectedRows.length === 0
                        ? "Check scheduled patients above to send them now."
                        : `Text ${selectedRows.length} selected patient${selectedRows.length === 1 ? "" : "s"} now and remove them from the reminder schedule.`
                    }
                    className="py-2.5 px-4 rounded-xl bg-sky-500/15 border border-sky-400/30 text-sky-100 text-sm font-semibold hover:bg-sky-500/25 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <Send className="w-4 h-4" />
                      Send Selected
                      {selectedRows.length ? ` (${selectedRows.length})` : ""}
                    </span>
                  </button>
                )}
                <button
                  onClick={() => {
                    setSendScope("all");
                    setConfirmSend(true);
                  }}
                  disabled={sending || scheduling || pending.length === 0}
                  className="group relative py-2.5 px-5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-white text-sm font-semibold ring-1 ring-white/20 hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="relative flex items-center justify-center gap-2">
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send
                      </>
                    )}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results summary */}
        {summary && (
          <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl p-5">
            <div className="flex items-center gap-4 mb-3">
              <span className="flex items-center gap-2 text-sm text-emerald-300">
                <CheckCircle2 className="w-4 h-4" />
                {summary.sent} sent
              </span>
              {summary.failed > 0 && (
                <span className="flex items-center gap-2 text-sm text-rose-300">
                  <XCircle className="w-4 h-4" />
                  {summary.failed} failed (kept in pending to retry)
                </span>
              )}
            </div>
            {summary.failed > 0 && (
              <div className="space-y-1">
                {summary.results
                  .filter((r) => !r.success)
                  .map((r, i) => (
                    <div
                      key={i}
                      className="text-xs text-rose-200/80 flex items-center gap-2"
                    >
                      <XCircle className="w-3 h-3 shrink-0" />
                      <span className="font-mono">{r.phone || "—"}</span>
                      <span className="text-rose-300/60">
                        {r.error || "failed"}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Recently Referred */}
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-purple-300/80" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold">
              Recently Referred
            </span>
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-slate-400">{recent.length}</span>
          </div>

          {recent.length === 0 ? (
            <p className="text-sm text-slate-500 py-2">
              Nothing sent yet. Sent numbers will appear here.
            </p>
          ) : (
            <div className="space-y-2">
              {recent.map((row) => (
                <div
                  key={row.id}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/10"
                >
                  <CheckCircle2 className="w-4 h-4 text-emerald-300/80 shrink-0" />
                  <span className="font-mono text-sm text-slate-100">
                    {row.phone}
                  </span>
                  {row.firstName && (
                    <span className="text-sm text-slate-400">
                      · {row.firstName}
                    </span>
                  )}
                  <span className="flex-1" />
                  <span className="text-xs text-slate-500">
                    {fmtTime(row.smsSentAt)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Send confirmation — sending real SMS to patients is not undoable. */}
      {confirmSend && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => !sending && setConfirmSend(false)}
          />
          <div className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-[#0d0a1f] shadow-[0_20px_60px_-20px_rgba(124,58,237,0.6)] p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/15 border border-purple-400/30 flex items-center justify-center shrink-0">
                <Send className="w-4 h-4 text-purple-300" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-white">
                  Send text messages?
                </h3>
                {sendScope === "selected" ? (
                  <p className="mt-1 text-sm text-slate-400">
                    This will text the {selectedRows.length} selected patient
                    {selectedRows.length === 1 ? "" : "s"} their questionnaire
                    link right now and remove them from the automatic reminder
                    schedule. This can't be undone.
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-slate-400">
                    This will text {sendableCount} patient
                    {sendableCount === 1 ? "" : "s"} their questionnaire link
                    right now. This can't be undone.
                  </p>
                )}
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setConfirmSend(false)}
                disabled={sending}
                className="px-3 py-2 rounded-xl bg-white/[0.05] border border-white/10 text-sm text-slate-200 hover:bg-white/[0.08] transition disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setConfirmSend(false);
                  handleSend(sendScope);
                }}
                disabled={sending}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-white text-sm font-semibold ring-1 ring-white/20 hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] transition disabled:opacity-60"
              >
                Yes, send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralPage;
